import { Box, Image, Text, VStack } from "@chakra-ui/react";

import { TicketsConvertedModalContent } from "./TicketsConvertedModalContent";

import { RoundedButton } from "@/components/rounded-button";
import { DialogRoot } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { useConvertRaffleTicketsToPP } from "@/hooks/useConvertRaffleTicketsToPP";
import { isDailySpinDeprecated } from "@/utils";

export const ConvertRaffleTicketsBanner = () => {
  const isDeprecated = isDailySpinDeprecated();
  const { isAuthenticated } = useAuth();
  const {
    convert,
    isConverting,
    isSuccess,
    conversionResult,
    resetSuccess,
    ticketsToConvert,
    expectedPP,
  } = useConvertRaffleTicketsToPP();

  const title = isDeprecated
    ? "Daily Spin has ended!"
    : "Final Days of Daily Spin — ending Feb 15, 2026";

  const description = isDeprecated
    ? `Convert ${ticketsToConvert} tickets to ${expectedPP} Plume Points`
    : "When the daily spin ends, you will be able to convert your tickets to Plume Points at a 10:1 ratio";

  const showConvertButton =
    isDeprecated && isAuthenticated && ticketsToConvert > 0;

  const handleCloseModal = () => {
    resetSuccess();
  };

  if (!isAuthenticated) {
    return null;
  }

  // Hide banner if user has no tickets and modal is not open
  // (keeps banner visible while success modal is shown, then hides after modal closes)
  if (ticketsToConvert === 0 && !isSuccess) {
    return null;
  }

  return (
    <>
      <Box
        bg="#1c0e39"
        borderRadius="16px"
        p={6}
        display="flex"
        gap={4}
        alignItems="center"
        w="full"
        overflow="hidden"
      >
        {/* Icons section */}
        <Box position="relative" flexShrink={0} w="82.302px" h="54.305px">
          {/* First icon - rotated */}
          <Box
            position="absolute"
            left={0}
            top={0}
            w="53.869px"
            h="53.869px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box transform="rotate(-27.23deg)">
              <Box
                bg="#896efd"
                borderRadius="12px"
                w="40px"
                h="40px"
                position="relative"
              >
                <Box
                  position="absolute"
                  left="7.5px"
                  top="7.5px"
                  w="25px"
                  h="25px"
                >
                  <Image
                    src="/assets/daily-spin/ticket-icon.svg"
                    alt=""
                    w="full"
                    h="full"
                    objectFit="contain"
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Second icon - rotated */}
          <Box
            position="absolute"
            left="30.93px"
            top="2.93px"
            w="51.37px"
            h="51.37px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box transform="rotate(20.24deg)">
              <Box w="40px" h="40px" position="relative">
                <Image
                  src="/assets/daily-spin/plume-points-icon.svg"
                  alt=""
                  w="full"
                  h="full"
                  objectFit="contain"
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Text content */}
        <VStack flex={1} alignItems="start" gap={0} minW={0}>
          <Text
            fontFamily="heading"
            fontWeight="500"
            fontSize="18px"
            lineHeight="24px"
            letterSpacing="-0.33px"
            color="white"
          >
            {title}
          </Text>
          <Text
            fontFamily="body"
            fontWeight="500"
            fontSize="16px"
            lineHeight="24px"
            letterSpacing="-0.18px"
            color="rgba(255,255,255,0.7)"
          >
            {description}
          </Text>
        </VStack>

        {/* Button */}
        {showConvertButton && (
          <Box flexShrink={0}>
            <RoundedButton
              variant="surface"
              px={4}
              py={2}
              h="44px"
              fontWeight="500"
              fontSize="14px"
              lineHeight="20px"
              letterSpacing="-0.09px"
              onClick={convert}
              loading={isConverting}
              disabled={isConverting}
            >
              Convert {ticketsToConvert} Tickets
            </RoundedButton>
          </Box>
        )}
      </Box>

      {/* Success Modal */}
      <DialogRoot
        size="sm"
        lazyMount
        open={isSuccess && !!conversionResult}
        onOpenChange={(e) => !e.open && handleCloseModal()}
      >
        {conversionResult && (
          <TicketsConvertedModalContent
            ticketsConverted={conversionResult.ticketsConverted}
            ppGained={conversionResult.ppGained}
            onClose={handleCloseModal}
          />
        )}
      </DialogRoot>
    </>
  );
};
